/**
 * ComponentStatus — per-component status pill with optional 90-day uptime dot grid.
 *
 * Status colours:
 *   green  = operational
 *   yellow = degraded
 *   orange = partial_outage
 *   red    = major_outage
 *   blue   = maintenance
 */

import type { ComponentStatus as ComponentStatusType, StatusComponent } from '../../api/status.js';

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  ComponentStatusType,
  { dot: string; bg: string; text: string; label: string; dotGrid: string }
> = {
  operational:    { dot: 'bg-emerald-500', bg: 'bg-emerald-50',  text: 'text-emerald-700', label: 'Operational',    dotGrid: 'bg-emerald-400' },
  degraded:       { dot: 'bg-amber-400',   bg: 'bg-amber-50',    text: 'text-amber-700',   label: 'Degraded',       dotGrid: 'bg-amber-400' },
  partial_outage: { dot: 'bg-orange-500',  bg: 'bg-orange-50',   text: 'text-orange-700',  label: 'Partial Outage', dotGrid: 'bg-orange-400' },
  major_outage:   { dot: 'bg-red-500',     bg: 'bg-red-50',      text: 'text-red-700',     label: 'Major Outage',   dotGrid: 'bg-red-500' },
  maintenance:    { dot: 'bg-blue-400',    bg: 'bg-blue-50',     text: 'text-blue-700',    label: 'Maintenance',    dotGrid: 'bg-blue-400' },
};

// ── Uptime dot grid (90-day) ──────────────────────────────────────────────────

interface UptimeDotGridProps {
  /** 0–100, null = no data */
  uptimePercentage: number | null;
  /** Number of days tracked */
  periodDays?: number;
}

function UptimeDotGrid({ uptimePercentage, periodDays = 90 }: UptimeDotGridProps) {
  if (uptimePercentage === null) return null;

  // Approximate which dots to show as outage (red) based on percentage
  const totalDots = Math.min(periodDays, 90);
  const outageDots = Math.round(((100 - uptimePercentage) / 100) * totalDots);
  const dots = Array.from({ length: totalDots }, (_, i) => i < totalDots - outageDots);

  return (
    <div className="flex gap-px flex-wrap mt-1" title={`${uptimePercentage.toFixed(2)}% uptime over ${totalDots} days`}>
      {dots.map((isUp, i) => (
        <div
          key={i}
          className={`w-1.5 h-3 rounded-sm ${isUp ? 'bg-emerald-400' : 'bg-red-400'}`}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

// ── ComponentStatus pill ──────────────────────────────────────────────────────

interface ComponentStatusProps {
  component: StatusComponent;
  /** Whether to show the 90-day uptime dot grid */
  showUptimeGrid?: boolean;
  className?: string;
}

export function ComponentStatus({ component, showUptimeGrid = false, className = '' }: ComponentStatusProps) {
  const config = STATUS_CONFIG[component.status] ?? STATUS_CONFIG.operational;

  return (
    <div className={`flex items-center justify-between gap-2 ${className}`}>
      <div className="flex items-center gap-2 min-w-0">
        {/* Status dot */}
        <span
          className={`flex-shrink-0 w-2.5 h-2.5 rounded-full ${config.dot}`}
          aria-hidden="true"
        />
        <span className="text-sm font-medium text-neutral-800 truncate">{component.name}</span>
      </div>

      <div className="flex flex-col items-end gap-0.5">
        {/* Status pill */}
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.text} ${config.bg}`}
        >
          {config.label}
        </span>

        {/* Uptime percentage */}
        {component.uptime_percentage !== null && (
          <span className="text-xs text-neutral-500">
            {component.uptime_percentage.toFixed(2)}% uptime
          </span>
        )}

        {/* 90-day uptime grid */}
        {showUptimeGrid && (
          <UptimeDotGrid
            uptimePercentage={component.uptime_percentage}
            periodDays={component.uptime_period_days ?? 90}
          />
        )}
      </div>
    </div>
  );
}

// ── Standalone status pill (for use outside component context) ────────────────

interface StatusPillProps {
  status: ComponentStatusType;
  size?: 'sm' | 'md';
}

export function StatusPill({ status, size = 'md' }: StatusPillProps) {
  const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.operational;
  const sizeClass = size === 'sm' ? 'text-xs px-1.5 py-0.5' : 'text-sm px-2.5 py-1';

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-medium ${sizeClass} ${config.text} ${config.bg}`}
    >
      <span className={`w-2 h-2 rounded-full ${config.dot}`} aria-hidden="true" />
      {config.label}
    </span>
  );
}
